import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shadcn/ui/select";
import { LOCALES, localeNativeName, type Locale } from "@repo/i18n";
import { useTranslation } from "react-i18next";
import { useLocale } from "../useLocale";

interface LanguageSwitcherProps {
  id?: string;
  className?: string;
  /** Runs after the UI language has switched, e.g. to persist the choice. */
  onChanged?: (locale: Locale) => void | Promise<void>;
}

/**
 * Picks the UI language. Switching is immediate and cached by the language
 * detector in localStorage; the account page also saves it to the profile.
 */
export const LanguageSwitcher = ({ id, className, onChanged }: LanguageSwitcherProps) => {
  const { t, i18n } = useTranslation("common");
  const locale = useLocale();

  const handleChange = async (next: Locale) => {
    if (next === locale) return;
    await i18n.changeLanguage(next);
    await onChanged?.(next);
  };

  return (
    <Select value={locale} onValueChange={(next) => next && void handleChange(next as Locale)}>
      <SelectTrigger id={id} className={className} aria-label={t("language")}>
        {/* base-ui renders the raw value unless given children */}
        <SelectValue>{localeNativeName[locale]}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {LOCALES.map((lng) => (
          <SelectItem key={lng} value={lng}>
            {localeNativeName[lng]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
